import type { LangMap } from "./index";

export const studio: LangMap = {
  en: {
    pageTitle: "Studio",
    pageSubtitle: "Timer, quizzes and flashcards.",
    error: "Error",
    modes: {
      pomodoro: { label: "Pomodoro", desc: "Focus blocks + breaks" },
      flowtime: { label: "Flowtime", desc: "Go as long as you're in flow" },
      countdown: { label: "Countdown", desc: "Classic timer down to 0" },
      stopwatch: { label: "Stopwatch", desc: "Free stopwatch" },
      libera: { label: "Free", desc: "No constraints" },
    },
    shortcuts: { flashcards: "Flashcards", quiz: "Quiz", stats: "Statistics" },
    whereToStart: {
      title: "Where do I start?",
      desc: "Ask the AI to suggest a priority based on your commitments.",
      cta: "Ask",
    },
    spotify: {
      title: "Study music",
      note: "You need a Spotify account (Free or Premium) for full playback.",
      presets: { alpha: "Alpha Waves", lofi: "Lo-Fi Study", classica: "Classical", ambient: "Ambient", piano: "Piano", nature: "Nature" },
    },
    session: {
      pause: "Pause",
      start: "Start",
      resetAria: "Reset timer",
      toastEnd: "Session: {{min}} min. Great job!",
      toastSaved: "Session: {{min}} min saved",
      runningNote: "The timer keeps running even if you close the app. You'll get a notification when it ends.",
      pausedNote: "Paused · {{min}} min {{sec}}s accumulated.",
    },
    sounds: {
      title: "Ambient sound",
      off: "Silence",
      white: "White noise",
      brown: "Brown noise",
      pink: "Pink noise",
      rain: "Rain",
      lofi: "Lo-fi",
      classica: "Classical",
      playError: "Unable to play audio (network)",
    },
  },
  it: {
    pageTitle: "Studio",
    pageSubtitle: "Timer, quiz e flashcard.",
    error: "Errore",
    modes: {
      pomodoro: { label: "Pomodoro", desc: "Blocchi di focus + pause" },
      flowtime: { label: "Flowtime", desc: "Vai finche' sei nel flow" },
      countdown: { label: "Countdown", desc: "Timer classico verso 0" },
      stopwatch: { label: "Stopwatch", desc: "Cronometro libero" },
      libera: { label: "Libera", desc: "Nessun vincolo" },
    },
    shortcuts: { flashcards: "Flashcard", quiz: "Quiz", stats: "Statistiche" },
    whereToStart: {
      title: "Da dove inizio?",
      desc: "Chiedi all'IA di suggerirti la priorità in base ai tuoi impegni.",
      cta: "Chiedi",
    },
    spotify: {
      title: "Musica per studiare",
      note: "Serve un account Spotify (Free o Premium) per l'ascolto completo.",
      presets: { alpha: "Onde Alpha", lofi: "Lo-Fi Study", classica: "Classica", ambient: "Ambient", piano: "Piano", nature: "Natura" },
    },
    session: {
      pause: "Pausa",
      start: "Avvia",
      resetAria: "Reimposta timer",
      toastEnd: "Sessione: {{min}} min. Bravissimo!",
      toastSaved: "Sessione: {{min}} min salvata",
      runningNote: "Il timer continua anche se chiudi l'app. Al termine ricevi una notifica.",
      pausedNote: "In pausa · {{min}} min {{sec}}s accumulati.",
    },
    sounds: {
      title: "Suono ambientale",
      off: "Silenzio",
      white: "Rumore bianco",
      brown: "Rumore marrone",
      pink: "Rumore rosa",
      rain: "Pioggia",
      lofi: "Lo-fi",
      classica: "Classica",
      playError: "Impossibile riprodurre l'audio (rete)",
    },
  },
  es: {
    pageTitle: "Estudio",
    pageSubtitle: "Temporizador, quiz y tarjetas.",
    error: "Error",
    modes: {
      pomodoro: { label: "Pomodoro", desc: "Bloques de enfoque + pausas" },
      flowtime: { label: "Flowtime", desc: "Sigue mientras estés en flujo" },
      countdown: { label: "Cuenta atrás", desc: "Temporizador clásico hacia 0" },
      stopwatch: { label: "Cronómetro", desc: "Cronómetro libre" },
      libera: { label: "Libre", desc: "Sin restricciones" },
    },
    shortcuts: { flashcards: "Tarjetas", quiz: "Quiz", stats: "Estadísticas" },
    whereToStart: {
      title: "¿Por dónde empiezo?",
      desc: "Pide a la IA que te sugiera la prioridad según tus compromisos.",
      cta: "Preguntar",
    },
    spotify: {
      title: "Música para estudiar",
      note: "Necesitas una cuenta de Spotify (Free o Premium) para la reproducción completa.",
      presets: { alpha: "Ondas Alfa", lofi: "Lo-Fi Study", classica: "Clásica", ambient: "Ambient", piano: "Piano", nature: "Naturaleza" },
    },
    session: {
      pause: "Pausa",
      start: "Iniciar",
      resetAria: "Reiniciar temporizador",
      toastEnd: "Sesión: {{min}} min. ¡Muy bien!",
      toastSaved: "Sesión: {{min}} min guardada",
      runningNote: "El temporizador sigue aunque cierres la app. Al terminar recibirás una notificación.",
      pausedNote: "En pausa · {{min}} min {{sec}}s acumulados.",
    },
    sounds: {
      title: "Sonido ambiental",
      off: "Silencio",
      white: "Ruido blanco",
      brown: "Ruido marrón",
      pink: "Ruido rosa",
      rain: "Lluvia",
      lofi: "Lo-fi",
      classica: "Clásica",
      playError: "No se pudo reproducir el audio (red)",
    },
  },
  fr: {
    pageTitle: "Studio",
    pageSubtitle: "Minuteur, quiz et cartes mémo.",
    error: "Erreur",
    modes: {
      pomodoro: { label: "Pomodoro", desc: "Blocs de concentration + pauses" },
      flowtime: { label: "Flowtime", desc: "Continue tant que tu es dans le flow" },
      countdown: { label: "Compte à rebours", desc: "Minuteur classique vers 0" },
      stopwatch: { label: "Chronomètre", desc: "Chronomètre libre" },
      libera: { label: "Libre", desc: "Aucune contrainte" },
    },
    shortcuts: { flashcards: "Cartes mémo", quiz: "Quiz", stats: "Statistiques" },
    whereToStart: {
      title: "Par où commencer ?",
      desc: "Demande à l'IA de te suggérer une priorité selon tes engagements.",
      cta: "Demander",
    },
    spotify: {
      title: "Musique pour étudier",
      note: "Un compte Spotify (Free ou Premium) est nécessaire pour l'écoute complète.",
      presets: { alpha: "Ondes Alpha", lofi: "Lo-Fi Study", classica: "Classique", ambient: "Ambiant", piano: "Piano", nature: "Nature" },
    },
    session: {
      pause: "Pause",
      start: "Démarrer",
      resetAria: "Réinitialiser le minuteur",
      toastEnd: "Session : {{min}} min. Bravo !",
      toastSaved: "Session : {{min}} min enregistrée",
      runningNote: "Le minuteur continue même si tu fermes l'application. Tu recevras une notification à la fin.",
      pausedNote: "En pause · {{min}} min {{sec}}s accumulées.",
    },
    sounds: {
      title: "Son ambiant",
      off: "Silence",
      white: "Bruit blanc",
      brown: "Bruit brun",
      pink: "Bruit rose",
      rain: "Pluie",
      lofi: "Lo-fi",
      classica: "Classique",
      playError: "Impossible de lire l'audio (réseau)",
    },
  },
  de: {
    pageTitle: "Studio",
    pageSubtitle: "Timer, Quiz und Karteikarten.",
    error: "Fehler",
    modes: {
      pomodoro: { label: "Pomodoro", desc: "Fokusblöcke + Pausen" },
      flowtime: { label: "Flowtime", desc: "Weiter, solange du im Flow bist" },
      countdown: { label: "Countdown", desc: "Klassischer Timer bis 0" },
      stopwatch: { label: "Stoppuhr", desc: "Freie Stoppuhr" },
      libera: { label: "Frei", desc: "Keine Einschränkungen" },
    },
    shortcuts: { flashcards: "Karteikarten", quiz: "Quiz", stats: "Statistiken" },
    whereToStart: {
      title: "Wo fange ich an?",
      desc: "Lass dir von der KI eine Priorität basierend auf deinen Terminen vorschlagen.",
      cta: "Fragen",
    },
    spotify: {
      title: "Musik zum Lernen",
      note: "Für die vollständige Wiedergabe benötigst du ein Spotify-Konto (Free oder Premium).",
      presets: { alpha: "Alphawellen", lofi: "Lo-Fi Study", classica: "Klassik", ambient: "Ambient", piano: "Klavier", nature: "Natur" },
    },
    session: {
      pause: "Pause",
      start: "Start",
      resetAria: "Timer zurücksetzen",
      toastEnd: "Sitzung: {{min}} Min. Sehr gut!",
      toastSaved: "Sitzung: {{min}} Min. gespeichert",
      runningNote: "Der Timer läuft weiter, auch wenn du die App schließt. Am Ende erhältst du eine Benachrichtigung.",
      pausedNote: "Pausiert · {{min}} Min {{sec}}s gesammelt.",
    },
    sounds: {
      title: "Umgebungsgeräusch",
      off: "Stille",
      white: "Weißes Rauschen",
      brown: "Braunes Rauschen",
      pink: "Rosa Rauschen",
      rain: "Regen",
      lofi: "Lo-fi",
      classica: "Klassik",
      playError: "Audio kann nicht abgespielt werden (Netzwerk)",
    },
  },
  pt: {
    pageTitle: "Studio",
    pageSubtitle: "Cronômetro, quiz e flashcards.",
    error: "Erro",
    modes: {
      pomodoro: { label: "Pomodoro", desc: "Blocos de foco + pausas" },
      flowtime: { label: "Flowtime", desc: "Continue enquanto estiver no flow" },
      countdown: { label: "Contagem regressiva", desc: "Cronômetro clássico até 0" },
      stopwatch: { label: "Cronômetro", desc: "Cronômetro livre" },
      libera: { label: "Livre", desc: "Sem restrições" },
    },
    shortcuts: { flashcards: "Flashcards", quiz: "Quiz", stats: "Estatísticas" },
    whereToStart: {
      title: "Por onde começo?",
      desc: "Peça à IA para sugerir a prioridade com base nos seus compromissos.",
      cta: "Perguntar",
    },
    spotify: {
      title: "Música para estudar",
      note: "É necessária uma conta Spotify (Free ou Premium) para a reprodução completa.",
      presets: { alpha: "Ondas Alfa", lofi: "Lo-Fi Study", classica: "Clássica", ambient: "Ambient", piano: "Piano", nature: "Natureza" },
    },
    session: {
      pause: "Pausar",
      start: "Iniciar",
      resetAria: "Reiniciar cronômetro",
      toastEnd: "Sessão: {{min}} min. Muito bem!",
      toastSaved: "Sessão: {{min}} min salva",
      runningNote: "O cronômetro continua mesmo se você fechar o app. Ao terminar você recebe uma notificação.",
      pausedNote: "Pausado · {{min}} min {{sec}}s acumulados.",
    },
    sounds: {
      title: "Som ambiente",
      off: "Silêncio",
      white: "Ruído branco",
      brown: "Ruído marrom",
      pink: "Ruído rosa",
      rain: "Chuva",
      lofi: "Lo-fi",
      classica: "Clássica",
      playError: "Não foi possível reproduzir o áudio (rede)",
    },
  },
};
